import {
  CloudServerOutlined,
  CloudUploadOutlined,
  HistoryOutlined,
  ReloadOutlined,
} from '@ant-design/icons';
import { Alert, Checkbox, Empty, Radio, Select, Skeleton, Table, Tag, message } from 'antd';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { apiService } from '../../services/api';
import { formatApiErrorDetail } from '../../utils/formatApiErrorDetail';
import { formatMskDateTime } from '../../utils/moscow-calendar-date';
import '../../styles/connections-install.css';
import { standNameAfterPreflight, standNameOnTargetChange } from './standName';

const STATUS_LABELS = {
  draft: 'Запускается',
  running: 'Выполняется',
  completed: 'Завершена',
  partially_completed: 'Частично',
  failed: 'С ошибкой',
  cancelled: 'Отменена',
};

const MODE_LABELS = {
  clean: 'Чистая машина',
  kraken: 'Перенос с Kraken',
  upgrade: 'Накат Midori',
};

const MODE_ACCOUNT_HINTS = {
  clean: 'Станет первым пользователем Midori — менеджер будет входить ею.',
  kraken: 'Учётка Kraken: ею снимается снимок модемов и прокси, и она же становится учёткой Midori.',
  upgrade: 'Существующая учётка Midori — менеджер уже входит ею.',
};

const SUDO_LABELS = {
  root: 'root',
  nopasswd: 'sudo без пароля',
  password: 'sudo по паролю SSH',
  none: 'нет прав',
};

const TERMINAL_STATUSES = new Set(['completed', 'partially_completed', 'failed', 'cancelled']);
const CANCELLABLE_PHASES = new Set(['preflight', 'upload']);

const STAGES = [
  { key: 'preflight', label: 'Предпроверка сервера' },
  { key: 'upload', label: 'Загрузка пакета' },
  { key: 'install', label: 'Установщик на сервере' },
  { key: 'panel_setup', label: 'Вход в Midori и имя стенда' },
  { key: 'register', label: 'Подключение в менеджере' },
  { key: 'recovery', label: 'Восстановление' },
  { key: 'done', label: 'Готово' },
];
const PHASE_ORDER = STAGES.map((stage) => stage.key);

const NEW_CONNECTION = 'new';
const POLL_INTERVAL_MS = 3000;

// Откуда SSH: из подключения, уже заведённого в менеджере, или из формы (новый сервер).
const SOURCE_CONNECTION = 'connection';
const SOURCE_FORM = 'form';

const EMPTY_FORM = {
  target: undefined,
  ssh_host: '',
  ssh_port: '22',
  ssh_username: 'root',
  ssh_password: '',
  ssh_private_key: '',
  stand_name: '',
  // Что форма подставила в название сама (из истории или с сервера) — standName.js; в запрос не уходит.
  stand_name_auto: '',
  package: undefined,
  connection: undefined,
  api_host: '',
  reserve_modems_count: '',
  account_username: '',
  account_password: '',
  run_recovery: true,
};

const errorText = (error) => {
  const data = error?.response?.data;
  return data?.message
    || data?.detail?.message
    || formatApiErrorDetail(error)
    || error?.message
    || 'Ошибка запроса';
};

const errorHint = (error) => {
  const data = error?.response?.data;
  return data?.hint || data?.detail?.hint || null;
};

const formatSize = (bytes) => {
  if (!bytes && bytes !== 0) return '—';
  return `${(bytes / (1024 * 1024)).toFixed(1)} МБ`;
};

const renderStatusTag = (status) => {
  const color = status === 'completed'
    ? 'success'
    : status === 'running' || status === 'draft'
      ? 'processing'
      : status === 'partially_completed'
        ? 'warning'
        : status === 'failed'
          ? 'error'
          : 'default';
  return <Tag color={color}>{STATUS_LABELS[status] || status}</Tag>;
};

const sshSignature = (form, source) => JSON.stringify(source === SOURCE_CONNECTION
  ? [SOURCE_CONNECTION, form.target]
  : [
    SOURCE_FORM,
    form.ssh_host.trim(),
    String(form.ssh_port).trim(),
    form.ssh_username.trim(),
    Boolean(form.ssh_password),
    Boolean(form.ssh_private_key.trim()),
  ]);

const targetLabel = (item) => {
  const access = item.ssh_ready
    ? `${item.ssh_username}@${item.ssh_host}:${item.ssh_port} · ${item.has_ssh_key ? 'ключ' : 'пароль'}`
    : 'SSH не сохранён';
  return `${item.name} — ${access}${item.is_active ? '' : ' · неактивно'}`;
};

const stageView = (stage, job) => {
  const report = job?.report?.[stage.key] || {};
  const status = job?.status;
  const terminal = TERMINAL_STATUSES.has(status);
  const current = PHASE_ORDER.indexOf(job?.phase);
  const index = PHASE_ORDER.indexOf(stage.key);

  if (stage.key === 'done') {
    if (status === 'completed') return { state: 'done', label: 'готово' };
    if (status === 'partially_completed') return { state: 'warning', label: 'частично' };
    if (status === 'failed') return { state: 'error', label: 'с ошибкой' };
    if (status === 'cancelled') return { state: 'skipped', label: 'отменено' };
    return { state: 'pending', label: 'ожидает' };
  }
  if (stage.key === 'recovery' && report.skipped) {
    return { state: 'skipped', label: report.reason || 'не нужно' };
  }
  if (!terminal && job?.phase === stage.key) {
    if (stage.key === 'upload' && report.progress) {
      return { state: 'running', label: `загрузка ${report.progress.percent ?? 0}%` };
    }
    if (stage.key === 'install') {
      return { state: 'running', label: report.step ? `шаг «${report.step}»` : 'запуск' };
    }
    return { state: 'running', label: 'выполняется' };
  }
  // Этап, на котором задача упала, — красный, даже если его процесс успел отчитаться `done`: установщик
  // «закончился» и тогда, когда вернул ошибку (установка №6, 29.09: «Установщик на сервере — готово» при коде 100).
  if (status === 'failed' && job?.phase === stage.key) {
    return { state: 'error', label: 'остановлено' };
  }
  if (report.done || (current > index && index >= 0)) {
    if (stage.key === 'install' && report.summary?.changes != null) {
      return { state: 'done', label: `изменений: ${report.summary.changes}` };
    }
    if (stage.key === 'register' && report.connection_id) {
      const verb = report.action === 'created' ? 'создано' : 'обновлено';
      return { state: report.warning ? 'warning' : 'done', label: `${verb} #${report.connection_id}` };
    }
    if (stage.key === 'recovery' && report.status) {
      const ok = report.status === 'completed';
      return { state: ok ? 'done' : 'warning', label: STATUS_LABELS[report.status] || report.status };
    }
    return { state: report.warning ? 'warning' : 'done', label: 'готово' };
  }
  if (terminal && job?.phase === stage.key) {
    if (status === 'cancelled') return { state: 'skipped', label: 'отменено' };
    return { state: status === 'partially_completed' ? 'warning' : 'error', label: 'остановлено' };
  }
  return { state: 'pending', label: 'ожидает' };
};

// SSH нового сервера — вводится руками; после установки сохранится в подключении.
const ManualSshFields = ({ form, setField, useKey, setUseKey, disabled }) => (
  <>
    <div className="conn-install-grid">
      <label className="conn-install-field conn-install-field--wide">
        <span className="conn-install-field__label">Адрес для SSH</span>
        <input
          className="conn-install-input"
          value={form.ssh_host}
          onChange={(event) => setField('ssh_host', event.target.value)}
          placeholder="IP или имя сервера"
          autoComplete="off"
          disabled={disabled}
        />
      </label>
      <label className="conn-install-field">
        <span className="conn-install-field__label">Порт</span>
        <input
          className="conn-install-input"
          value={form.ssh_port}
          onChange={(event) => setField('ssh_port', event.target.value.replace(/\D/g, '').slice(0, 5))}
          inputMode="numeric"
          disabled={disabled}
        />
      </label>
      <label className="conn-install-field">
        <span className="conn-install-field__label">Пользователь</span>
        <input
          className="conn-install-input"
          value={form.ssh_username}
          onChange={(event) => setField('ssh_username', event.target.value)}
          autoComplete="off"
          disabled={disabled}
        />
      </label>
      <label className="conn-install-field conn-install-field--wide">
        <span className="conn-install-field__label">Пароль SSH</span>
        <input
          className="conn-install-input"
          type="password"
          value={form.ssh_password}
          onChange={(event) => setField('ssh_password', event.target.value)}
          autoComplete="new-password"
          placeholder={useKey ? 'не обязателен при входе по ключу; нужен для sudo' : ''}
          disabled={disabled}
        />
      </label>
    </div>
    <Checkbox
      className="conn-install-check"
      checked={useKey}
      onChange={(event) => {
        setUseKey(event.target.checked);
        if (!event.target.checked) setField('ssh_private_key', '');
      }}
      disabled={disabled}
    >
      Войти по приватному ключу
    </Checkbox>
    {useKey ? (
      <label className="conn-install-field conn-install-field--wide">
        <span className="conn-install-field__label">Приватный ключ (PEM / OpenSSH)</span>
        <textarea
          className="conn-install-input conn-install-textarea"
          value={form.ssh_private_key}
          onChange={(event) => setField('ssh_private_key', event.target.value)}
          placeholder="-----BEGIN OPENSSH PRIVATE KEY-----"
          spellCheck={false}
          disabled={disabled}
        />
      </label>
    ) : null}
  </>
);

const ConnectionsInstall = () => {
  const [form, setForm] = useState(EMPTY_FORM);
  const [sshSource, setSshSource] = useState(SOURCE_CONNECTION);
  const [targets, setTargets] = useState([]);
  const [canDecryptSsh, setCanDecryptSsh] = useState(true);
  const [useKey, setUseKey] = useState(false);
  const [packages, setPackages] = useState([]);
  const [packagesDir, setPackagesDir] = useState('');
  const [defaultReserve, setDefaultReserve] = useState(3);
  const [packagesLoading, setPackagesLoading] = useState(true);
  const [connections, setConnections] = useState([]);
  const [preflight, setPreflight] = useState(null);
  const [preflightSignature, setPreflightSignature] = useState(null);
  const [preflightError, setPreflightError] = useState(null);
  const [preflightLoading, setPreflightLoading] = useState(false);
  const [startLoading, setStartLoading] = useState(false);
  const [cancelLoading, setCancelLoading] = useState(false);
  const [jobs, setJobs] = useState([]);
  const [jobsLoading, setJobsLoading] = useState(true);
  const [activeJobId, setActiveJobId] = useState(null);
  const [activeJob, setActiveJob] = useState(null);
  const logRef = useRef(null);

  const setField = (name, value) => setForm((prev) => ({ ...prev, [name]: value }));

  const loadPackages = useCallback(async () => {
    setPackagesLoading(true);
    try {
      const { data } = await apiService.listMidoriPackages();
      const list = data?.packages || [];
      setPackages(list);
      setPackagesDir(data?.packages_dir || '');
      if (data?.default_reserve_modems != null) {
        setDefaultReserve(data.default_reserve_modems);
      }
      setForm((prev) => (prev.package || list.length === 0 ? prev : { ...prev, package: list[0].name }));
    } catch (error) {
      message.error(`Пакеты Midori: ${errorText(error)}`);
    } finally {
      setPackagesLoading(false);
    }
  }, []);

  const loadConnections = useCallback(async () => {
    try {
      const { data } = await apiService.getKrakenConnections();
      setConnections(Array.isArray(data) ? data : (data?.connections || []));
    } catch (_error) {
      setConnections([]);
    }
    try {
      const { data } = await apiService.listMidoriInstallConnections();
      setTargets(data?.connections || []);
      setCanDecryptSsh(data?.manager_can_decrypt_ssh !== false);
    } catch (_error) {
      setTargets([]);
    }
  }, []);

  const loadJobs = useCallback(async ({ pickActive = false } = {}) => {
    setJobsLoading(true);
    try {
      const { data } = await apiService.listMidoriInstallJobs(20);
      const list = data?.jobs || [];
      setJobs(list);
      if (pickActive && list.length > 0) {
        const running = list.find((job) => job.is_active);
        const chosen = running || list[0];
        setActiveJobId(chosen.id);
        setActiveJob(chosen);
      }
    } catch (error) {
      message.error(`История установок: ${errorText(error)}`);
    } finally {
      setJobsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadPackages();
    loadConnections();
    loadJobs({ pickActive: true });
  }, [loadPackages, loadConnections, loadJobs]);

  useEffect(() => {
    if (!activeJobId || (activeJob && TERMINAL_STATUSES.has(activeJob.status))) {
      return undefined;
    }
    const intervalId = window.setInterval(async () => {
      try {
        const { data } = await apiService.getMidoriInstallJob(activeJobId);
        const next = data?.job;
        if (!next) return;
        setActiveJob(next);
        if (TERMINAL_STATUSES.has(next.status)) {
          loadJobs();
          loadConnections();
        }
      } catch (_error) {
        // опрос живёт до следующей попытки
      }
    }, POLL_INTERVAL_MS);
    return () => window.clearInterval(intervalId);
  }, [activeJob, activeJobId, loadConnections, loadJobs]);

  const logTail = activeJob?.report?.install?.log_tail;
  useEffect(() => {
    if (logRef.current) {
      logRef.current.scrollTop = logRef.current.scrollHeight;
    }
  }, [logTail]);

  const fromConnection = sshSource === SOURCE_CONNECTION;
  const selectedTarget = fromConnection ? targets.find((item) => item.id === form.target) : null;
  const currentSignature = sshSignature(form, sshSource);
  const preflightFresh = Boolean(preflight) && preflightSignature === currentSignature;
  const mode = preflightFresh ? preflight.mode : null;
  const selectedConnection = form.connection && form.connection !== NEW_CONNECTION
    ? connections.find((item) => item.id === form.connection)
    : null;
  const creatingConnection = form.connection === NEW_CONNECTION;
  const accountRequired = creatingConnection;
  const accountFilled = Boolean(form.account_username.trim() && form.account_password);
  // Список подключений отдаёт не все поля (логина в нём нет) — логин берём из предпроверки.
  const selectedConnectionLogin = selectedConnection
    ? (selectedConnection.username
      || (preflightFresh
        ? [preflight?.suggested_connection, ...(preflight?.candidates || [])]
          .find((item) => item && item.id === selectedConnection.id)?.username
        : null))
    : null;
  const selectedPackage = packages.find((item) => item.name === form.package);
  const jobRunning = Boolean(activeJob && !TERMINAL_STATUSES.has(activeJob.status));

  const blockers = useMemo(() => {
    const list = [];
    if (!preflightFresh) list.push('проверьте сервер');
    else if (!preflight.ready_to_run) list.push('сервер не готов — см. предпроверку');
    if (!form.stand_name.trim()) list.push('укажите название стенда');
    if (!form.package) list.push('выберите пакет');
    else if (selectedPackage && selectedPackage.sha256_ok === false) list.push('пакет не сошёлся по sha256');
    if (!form.connection) list.push('выберите подключение или «создать новое»');
    if (accountRequired && !accountFilled) list.push('введите учётку менеджера');
    if (!accountRequired && form.account_password && !form.account_username.trim() && !selectedConnection) {
      list.push('укажите логин учётки');
    }
    return list;
  }, [preflightFresh, preflight, form, selectedPackage, accountRequired, accountFilled, selectedConnection]);

  const switchSource = (next) => {
    setSshSource(next);
    setForm((prev) => ({ ...prev, connection: undefined }));
    setPreflight(null);
    setPreflightSignature(null);
    setPreflightError(null);
  };

  const chooseTarget = (value) => {
    const item = targets.find((entry) => entry.id === value);
    // Повторный накат: название стенда — из прошлой установки на это подключение.
    setForm((prev) => {
      const [standName, auto] = standNameOnTargetChange(prev.stand_name, prev.stand_name_auto, item);
      return { ...prev, target: value, stand_name: standName, stand_name_auto: auto };
    });
  };

  const sshPayload = () => (fromConnection
    ? { ssh_source: SOURCE_CONNECTION, connection_id: form.target }
    : {
      ssh_source: SOURCE_FORM,
      ssh_host: form.ssh_host.trim(),
      ssh_port: Number(form.ssh_port) || 22,
      ssh_username: form.ssh_username.trim() || 'root',
      ssh_password: form.ssh_password || undefined,
      ssh_private_key: form.ssh_private_key.trim() || undefined,
    });

  const handlePreflight = async () => {
    if (fromConnection) {
      if (!form.target) {
        message.warning('Выберите подключение');
        return;
      }
    } else {
      if (!form.ssh_host.trim()) {
        message.warning('Укажите адрес сервера');
        return;
      }
      if (!form.ssh_password && !form.ssh_private_key.trim()) {
        message.warning('Укажите пароль SSH или ключ');
        return;
      }
    }
    setPreflightLoading(true);
    setPreflightError(null);
    const signature = sshSignature(form, sshSource);
    try {
      const { data } = await apiService.midoriInstallPreflight(sshPayload());
      const result = data?.preflight;
      setPreflight(result);
      setPreflightSignature(signature);
      // Midori уже стоит — название, которое стенд показывает сейчас (набранное пока шла проверка — не трогаем).
      setForm((prev) => {
        const [standName, auto] = standNameAfterPreflight(
          prev.stand_name, prev.stand_name_auto, result?.installed_stand_name,
        );
        return {
          ...prev,
          connection: result?.suggested_connection?.id ?? NEW_CONNECTION,
          stand_name: standName,
          stand_name_auto: auto,
        };
      });
      loadConnections();
    } catch (error) {
      setPreflight(null);
      setPreflightSignature(null);
      setPreflightError({ message: errorText(error), hint: errorHint(error) });
    } finally {
      setPreflightLoading(false);
    }
  };

  const handleStart = async () => {
    if (blockers.length > 0) {
      message.warning(`Нельзя запустить: ${blockers.join('; ')}`);
      return;
    }
    setStartLoading(true);
    try {
      const reserve = form.reserve_modems_count === '' ? undefined : Number(form.reserve_modems_count);
      const { data } = await apiService.startMidoriInstall({
        ...sshPayload(),
        package: form.package,
        // Хеш из списка: под тем же именем на диске может лежать уже другой файл — сервер откажет.
        package_sha256: selectedPackage?.sha256 || undefined,
        stand_name: form.stand_name.trim(),
        connection_id: fromConnection ? form.target : (creatingConnection ? null : form.connection),
        api_host: creatingConnection && form.api_host.trim() ? form.api_host.trim() : undefined,
        reserve_modems_count: creatingConnection ? reserve : undefined,
        account_username: form.account_username.trim() || undefined,
        account_password: form.account_password || undefined,
        run_recovery: Boolean(form.run_recovery),
      });
      const job = data?.job;
      if (job) {
        setActiveJobId(job.id);
        setActiveJob(job);
        message.success(`Установка #${job.id} запущена`);
      }
      // Секреты не держим в форме дольше нужного.
      setForm((prev) => ({ ...prev, ssh_password: '', ssh_private_key: '', account_password: '' }));
      setPreflight(null);
      setPreflightSignature(null);
      loadJobs();
    } catch (error) {
      const hint = errorHint(error);
      message.error(hint ? `${errorText(error)} ${hint}` : errorText(error), 8);
    } finally {
      setStartLoading(false);
    }
  };

  const handleCancel = async () => {
    if (!activeJob) return;
    setCancelLoading(true);
    try {
      const { data } = await apiService.cancelMidoriInstallJob(activeJob.id);
      if (data?.job) setActiveJob(data.job);
      message.info('Отмена принята — задача остановится до запуска установщика');
    } catch (error) {
      message.error(errorText(error));
    } finally {
      setCancelLoading(false);
    }
  };

  const connectionOptions = useMemo(() => {
    const suggestedId = preflightFresh ? preflight?.suggested_connection?.id : null;
    const candidateIds = new Set((preflightFresh ? preflight?.candidates || [] : []).map((item) => item.id));
    const options = [{ value: NEW_CONNECTION, label: 'Создать новое подключение' }];
    connections.forEach((item) => {
      const marks = [];
      if (item.id === suggestedId) marks.push('найдено по SSH');
      else if (candidateIds.has(item.id)) marks.push('тот же адрес');
      if (!item.is_active) marks.push('неактивно');
      options.push({
        value: item.id,
        label: `${item.name} — ${item.server_url}${marks.length ? ` (${marks.join(', ')})` : ''}`,
      });
    });
    return options;
  }, [connections, preflight, preflightFresh]);

  const jobColumns = useMemo(() => [
    { title: '#', dataIndex: 'id', key: 'id', width: 64 },
    {
      title: 'Начало',
      dataIndex: 'created_at',
      key: 'created_at',
      width: 160,
      render: (value) => formatMskDateTime(value),
    },
    {
      title: 'Сервер',
      key: 'target',
      render: (_, job) => `${job.target_user}@${job.target_host}:${job.target_port}`,
    },
    { title: 'Стенд', dataIndex: 'stand_name', key: 'stand_name' },
    {
      title: 'Режим',
      dataIndex: 'mode',
      key: 'mode',
      width: 150,
      render: (value) => MODE_LABELS[value] || '—',
    },
    {
      title: 'Пакет',
      key: 'package',
      render: (_, job) => job.package?.name || '—',
    },
    {
      title: 'Статус',
      dataIndex: 'status',
      key: 'status',
      width: 130,
      render: (value) => renderStatusTag(value),
    },
    { title: 'Кто', dataIndex: 'initiated_by', key: 'initiated_by', width: 140 },
    {
      title: '',
      key: 'open',
      width: 100,
      render: (_, job) => (
        <button
          type="button"
          className="conn-header__action-btn"
          style={{ width: 'auto', padding: '0 12px' }}
          onClick={() => {
            setActiveJobId(job.id);
            setActiveJob(job);
          }}
        >
          Открыть
        </button>
      ),
    },
  ], []);

  const activeCount = jobs.filter((job) => job.is_active).length;
  const lastJob = jobs[0];
  const result = activeJob?.report?.result;
  const installReport = activeJob?.report?.install || {};
  const panelReport = activeJob?.report?.panel_setup || {};
  const registerReport = activeJob?.report?.register || {};
  const recoveryReport = activeJob?.report?.recovery || {};

  return (
    <div className="conn-recovery-page conn-install-page">
      <div className="conn-recovery-summary">
        <div className="conn-recovery-summary__card">
          <span className="conn-recovery-summary__icon">
            <CloudUploadOutlined />
          </span>
          <div>
            <div className="conn-recovery-summary__value">{packages.length}</div>
            <div className="conn-recovery-summary__label">
              Пакетов Midori{packagesDir ? ` в ${packagesDir}` : ''}
            </div>
          </div>
        </div>
        <div className="conn-recovery-summary__card">
          <span className="conn-recovery-summary__icon conn-recovery-summary__icon--success">
            <CloudServerOutlined />
          </span>
          <div>
            <div className="conn-recovery-summary__value">{activeCount}</div>
            <div className="conn-recovery-summary__label">Установок идёт сейчас</div>
          </div>
        </div>
        <div className="conn-recovery-summary__card">
          <span className="conn-recovery-summary__icon conn-recovery-summary__icon--muted">
            <HistoryOutlined />
          </span>
          <div>
            <div className="conn-recovery-summary__value">
              {lastJob ? STATUS_LABELS[lastJob.status] || lastJob.status : '—'}
            </div>
            <div className="conn-recovery-summary__label">
              {lastJob ? `Последняя: ${lastJob.target_host}, ${formatMskDateTime(lastJob.created_at)}` : 'Установок ещё не было'}
            </div>
          </div>
        </div>
      </div>

      <Alert
        className="conn-recovery-alert"
        type="info"
        showIcon
        message="Установка Midori на сервер по SSH"
        description="Чистая машина — установка с нуля. Сервер с Kraken — перенос модемов, прокси и клиентов с теми же id и портами, затем «Восстановление» автоматически. Сервер с Midori — накат выбранной версии. Установщик идёт на сервере сам: обрыв связи с менеджером его не прерывает."
      />

      <div className="conn-recovery-grid">
        <section className="conn-recovery-card">
          <div className="conn-recovery-card__header">
            <h2 className="conn-recovery-card__title">Параметры установки</h2>
            <button
              type="button"
              className={`conn-header__action-btn${packagesLoading ? ' conn-header__action-btn--loading' : ''}`}
              onClick={() => {
                loadPackages();
                loadConnections();
                loadJobs();
              }}
              disabled={packagesLoading}
              aria-label="Обновить пакеты и историю"
            >
              <ReloadOutlined />
            </button>
          </div>

          <div className="conn-install-section">
            <div className="conn-install-section__title">Сервер</div>
            <Radio.Group
              className="conn-install-source"
              value={sshSource}
              onChange={(event) => switchSource(event.target.value)}
              disabled={jobRunning}
              optionType="button"
              options={[
                { value: SOURCE_CONNECTION, label: 'Из списка подключений' },
                { value: SOURCE_FORM, label: 'Новый сервер' },
              ]}
            />
            {fromConnection ? (
              <label className="conn-install-field conn-install-field--wide" htmlFor="midori-install-target">
                <span className="conn-install-field__label">Подключение</span>
                <Select
                  id="midori-install-target"
                  value={form.target}
                  onChange={chooseTarget}
                  disabled={jobRunning}
                  placeholder={targets.length ? 'Выберите сервер' : 'Подключений нет — выберите «Новый сервер»'}
                  showSearch
                  optionFilterProp="label"
                  options={targets.map((item) => ({
                    value: item.id,
                    label: targetLabel(item),
                    disabled: !item.ssh_ready,
                  }))}
                />
                <span className="conn-install-field__note">
                  {canDecryptSsh
                    ? 'Вход — SSH, сохранённым в подключении: тем же менеджер ходит за band, IMEI и перезагрузкой. Подключение без сохранённого SSH выбрать нельзя — для него «Новый сервер».'
                    : 'На менеджере не задан SSH_ENCRYPTION_KEY — сохранённый SSH подключений не прочитать. Выберите «Новый сервер» и введите SSH.'}
                </span>
              </label>
            ) : (
              <ManualSshFields
                form={form}
                setField={setField}
                useKey={useKey}
                setUseKey={setUseKey}
                disabled={jobRunning}
              />
            )}
            <div className="conn-recovery-actions">
              <button
                type="button"
                className={`conn-header__action-btn${preflightLoading ? ' conn-header__action-btn--loading' : ''}`}
                style={{ width: 'auto', padding: '0 12px' }}
                onClick={handlePreflight}
                disabled={preflightLoading || jobRunning}
              >
                {preflightLoading ? 'Проверяю…' : 'Проверить сервер'}
              </button>
            </div>

            {preflightError ? (
              <div className="conn-recovery-block conn-install-block--error">
                <strong>{preflightError.message}</strong>
                {preflightError.hint ? <p className="conn-install-hint">{preflightError.hint}</p> : null}
              </div>
            ) : null}

            {preflight ? (
              <div className="conn-recovery-block">
                <div className="conn-recovery-block__header">
                  <span className="conn-install-mode">{MODE_LABELS[preflight.mode] || preflight.mode}</span>
                  {preflight.ready_to_run ? <Tag color="success">Готов</Tag> : <Tag color="error">Не готов</Tag>}
                </div>
                {!preflightFresh ? (
                  <p className="conn-install-hint conn-install-hint--warn">
                    Данные сервера изменились после проверки — проверьте снова.
                  </p>
                ) : null}
                <p className="conn-recovery-placeholder__text">
                  {preflight.os?.pretty_name || 'ОС не определена'} · {preflight.hostname || preflight.host} ·
                  {' '}{SUDO_LABELS[preflight.sudo_mode] || preflight.sudo_mode}
                  {preflight.installed_http_port
                    ? ` · Midori${preflight.installed_version ? ` ${preflight.installed_version}` : ''} на порту ${preflight.installed_http_port}`
                    : ''}
                </p>
                {(preflight.blocking_issues || []).length > 0 ? (
                  <ul className="conn-recovery-checklist conn-install-list--error">
                    {preflight.blocking_issues.map((item) => <li key={item.code}>{item.message}</li>)}
                  </ul>
                ) : null}
                {(preflight.warnings || []).length > 0 ? (
                  <ul className="conn-recovery-checklist">
                    {preflight.warnings.map((item) => <li key={item.code}>{item.message}</li>)}
                  </ul>
                ) : null}
              </div>
            ) : null}
          </div>

          <div className="conn-install-section">
            <div className="conn-install-section__title">Midori</div>
            <label className="conn-install-field conn-install-field--wide">
              <span className="conn-install-field__label">Название стенда</span>
              <input
                className="conn-install-input"
                value={form.stand_name}
                maxLength={64}
                onChange={(event) => setField('stand_name', event.target.value)}
                placeholder="Например, СПб — стойка 2"
                disabled={jobRunning}
              />
              <span className="conn-install-field__note">В шапке панели Midori и на странице входа.</span>
            </label>
            <label className="conn-install-field conn-install-field--wide" htmlFor="midori-install-package">
              <span className="conn-install-field__label">Пакет</span>
              <Select
                id="midori-install-package"
                value={form.package}
                onChange={(value) => setField('package', value)}
                loading={packagesLoading}
                disabled={jobRunning}
                placeholder={packagesDir ? `Положите пакет в ${packagesDir}` : 'Пакетов нет'}
                options={packages.map((item) => ({
                  value: item.name,
                  label: `${item.version} · ${formatSize(item.size)} · sha256 ${item.sha256.slice(0, 12)}…${item.sha256_ok === false ? ' · ⚠ не сошёлся' : ''}`,
                }))}
              />
            </label>
          </div>

          <div className="conn-install-section">
            <div className="conn-install-section__title">Подключение в менеджере</div>
            {fromConnection ? (
              <p className="conn-install-hint">
                {selectedTarget
                  ? `Обновится подключение «${selectedTarget.name}»: SSH, учётка и порт; имя, город и диапазоны не меняются.`
                  : 'Обновится подключение, выбранное выше.'}
              </p>
            ) : (
              <label className="conn-install-field conn-install-field--wide" htmlFor="midori-install-connection">
                <span className="conn-install-field__label">Подключение</span>
                <Select
                  id="midori-install-connection"
                  value={form.connection}
                  onChange={(value) => setField('connection', value)}
                  disabled={jobRunning}
                  placeholder="Сначала проверьте сервер"
                  options={connectionOptions}
                />
                <span className="conn-install-field__note">
                  Существующее подключение не меняет имени, города и диапазонов — обновляются SSH, учётка и порт.
                </span>
              </label>
            )}
            {creatingConnection ? (
              <div className="conn-install-grid">
                <label className="conn-install-field conn-install-field--wide">
                  <span className="conn-install-field__label">Адрес API для менеджера</span>
                  <input
                    className="conn-install-input"
                    value={form.api_host}
                    onChange={(event) => setField('api_host', event.target.value)}
                    placeholder={form.ssh_host || 'как адрес SSH'}
                    disabled={jobRunning}
                  />
                </label>
                <label className="conn-install-field">
                  <span className="conn-install-field__label">Резервных модемов</span>
                  <input
                    className="conn-install-input"
                    value={form.reserve_modems_count}
                    onChange={(event) => setField('reserve_modems_count', event.target.value.replace(/\D/g, '').slice(0, 3))}
                    placeholder={String(defaultReserve)}
                    inputMode="numeric"
                    disabled={jobRunning}
                  />
                </label>
              </div>
            ) : null}
          </div>

          <div className="conn-install-section">
            <div className="conn-install-section__title">Учётка менеджера на сервере</div>
            <p className="conn-install-hint">
              {mode ? MODE_ACCOUNT_HINTS[mode] : 'Логин и пароль, которыми менеджер будет входить в Midori.'}
              {selectedConnection
                ? ` Можно не заполнять — возьмётся из подключения «${selectedConnection.name}»${selectedConnectionLogin ? ` (логин ${selectedConnectionLogin})` : ''}.`
                : ''}
            </p>
            <div className="conn-install-grid">
              <label className="conn-install-field">
                <span className="conn-install-field__label">Логин</span>
                <input
                  className="conn-install-input"
                  value={form.account_username}
                  onChange={(event) => setField('account_username', event.target.value)}
                  autoComplete="off"
                  disabled={jobRunning}
                />
              </label>
              <label className="conn-install-field conn-install-field--wide">
                <span className="conn-install-field__label">Пароль</span>
                <input
                  className="conn-install-input"
                  type="password"
                  value={form.account_password}
                  onChange={(event) => setField('account_password', event.target.value)}
                  autoComplete="new-password"
                  disabled={jobRunning}
                />
              </label>
            </div>
            {mode === 'kraken' ? (
              <Checkbox
                className="conn-install-check"
                checked={form.run_recovery}
                onChange={(event) => setField('run_recovery', event.target.checked)}
                disabled={jobRunning}
              >
                После переноса запустить «Восстановление» (без сброса настроек модемов)
              </Checkbox>
            ) : null}
          </div>

          <div className="conn-recovery-actions conn-install-actions">
            <button
              type="button"
              className={`conn-header__action-btn conn-header__action-btn--primary${startLoading ? ' conn-header__action-btn--loading' : ''}`}
              style={{ width: 'auto', padding: '0 16px' }}
              onClick={handleStart}
              disabled={startLoading || jobRunning || blockers.length > 0}
            >
              Установить Midori
            </button>
            {blockers.length > 0 && !jobRunning ? (
              <span className="conn-install-hint">Осталось: {blockers.join('; ')}</span>
            ) : null}
            {jobRunning ? <span className="conn-install-hint">Идёт установка #{activeJob.id}</span> : null}
          </div>
        </section>

        <section className="conn-recovery-card">
          <div className="conn-recovery-card__header">
            <h2 className="conn-recovery-card__title">Ход установки</h2>
            {jobRunning && CANCELLABLE_PHASES.has(activeJob.phase) && !activeJob.cancel_requested ? (
              <button
                type="button"
                className={`conn-header__action-btn${cancelLoading ? ' conn-header__action-btn--loading' : ''}`}
                style={{ width: 'auto', padding: '0 12px' }}
                onClick={handleCancel}
                disabled={cancelLoading}
              >
                Отменить
              </button>
            ) : null}
          </div>
          {activeJob ? (
            <div className="conn-recovery-block">
              <div className="conn-recovery-block__header">
                <strong>Установка #{activeJob.id}</strong>
                {renderStatusTag(activeJob.status)}
              </div>
              <p className="conn-recovery-placeholder__text">
                {activeJob.target_user}@{activeJob.target_host}:{activeJob.target_port} · стенд «{activeJob.stand_name}»
                {activeJob.mode ? ` · ${MODE_LABELS[activeJob.mode] || activeJob.mode}` : ''}
              </p>
              <p className="conn-recovery-placeholder__text">
                Старт: {formatMskDateTime(activeJob.started_at || activeJob.created_at)} | Завершение: {formatMskDateTime(activeJob.finished_at)}
              </p>
              <div className="conn-recovery-stage-list">
                {STAGES.map((stage) => {
                  const view = stageView(stage, activeJob);
                  const modifier = view.state === 'pending' ? '' : ` conn-recovery-stage--${view.state}`;
                  return (
                    <div key={stage.key} className={`conn-recovery-stage${modifier}`}>
                      <span>{stage.label}</span>
                      <span>{view.label}</span>
                    </div>
                  );
                })}
              </div>

              {(activeJob.errors || []).length > 0 ? (
                <div className="conn-install-errors">
                  {activeJob.errors.map((item, index) => (
                    <div key={`${item.code}-${index}`} className="conn-install-error">
                      <strong>{item.message}</strong>
                      {item.hint ? <p className="conn-install-hint">{item.hint}</p> : null}
                    </div>
                  ))}
                </div>
              ) : null}

              {result ? (
                <div className="conn-install-result">
                  {result.panel_url ? (
                    <div>
                      Панель Midori:{' '}
                      <a href={result.panel_url} target="_blank" rel="noopener noreferrer">{result.panel_url}</a>
                    </div>
                  ) : null}
                  <div>
                    Подключение #{result.connection_id} — {result.connection_action === 'created' ? 'создано' : 'обновлено'};
                    {' '}модемов {result.modems_count ?? '—'}, прокси {result.proxies_count ?? '—'}
                  </div>
                  <div>
                    Пакет {result.package_version || '—'}, порт {result.http_port || '—'}; изменений {result.changes ?? '—'},
                    {' '}предупреждений {result.warnings_count ?? '—'}
                  </div>
                  {activeJob.recovery_run_id ? (
                    <div>
                      «Восстановление» #{activeJob.recovery_run_id}: {STATUS_LABELS[recoveryReport.status] || recoveryReport.status || '—'}
                      {recoveryReport.errors_count ? `, ошибок ${recoveryReport.errors_count}` : ''}
                    </div>
                  ) : null}
                  {panelReport.stand_name_shown ? (
                    <div>Имя стенда в Midori: «{panelReport.stand_name_shown}»</div>
                  ) : null}
                </div>
              ) : null}
              {registerReport.warning ? <p className="conn-install-hint conn-install-hint--warn">{registerReport.warning}</p> : null}
              {panelReport.warning ? <p className="conn-install-hint conn-install-hint--warn">{panelReport.warning.message}</p> : null}

              {(logTail || []).length > 0 ? (
                <>
                  <p className="conn-recovery-placeholder__text conn-install-log__caption">
                    Журнал установщика ({installReport.log_path || '/var/log/midori-install.log'})
                    {installReport.reconnects ? ` · переподключений: ${installReport.reconnects}` : ''}
                  </p>
                  <pre className="conn-install-log" ref={logRef}>{logTail.join('\n')}</pre>
                </>
              ) : null}
            </div>
          ) : (
            <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="Установок ещё не было" />
          )}
        </section>
      </div>

      <section className="conn-recovery-card">
        <div className="conn-recovery-card__header">
          <h2 className="conn-recovery-card__title">История установок</h2>
        </div>
        {jobsLoading && jobs.length === 0 ? (
          <Skeleton active paragraph={{ rows: 4 }} />
        ) : (
          <Table
            rowKey="id"
            columns={jobColumns}
            dataSource={jobs}
            pagination={false}
            locale={{ emptyText: 'Установок пока не было' }}
            scroll={{ x: 1100 }}
          />
        )}
      </section>
    </div>
  );
};

export default ConnectionsInstall;
